import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { PipelineMasterSteps } from 'global-stores';
import { ActionList } from 'views/complex-onboarding-flow/constant';
import {
	EdgesState,
	NodesState,
} from 'views/complex-onboarding-flow/stores/states';
import { useOnboardingHook } from 'views/onboarding-flow';
import { ComplexSettingFormState } from 'views/pipeline';
import { CustomHandle } from '../custom-handle';
import { ICustomNode } from '../custom-node';
import { RenderAccBlock } from './render-accreditation-block';
// __simplici_accreditation_tile_choice__: custom-node imports the tile
// components by file path, so a folder index.ts is not where a stylesheet gets
// picked up. Import it where the component is.
import './accreditation-action.scss';
import { TileTotal } from '../tile-total';

export const AccreditationWrapper = ({ children }: { children: ReactNode }) => {
	return <div className="accreditation-action">{children}</div>;
};

export const AccreditationAction: FC<ICustomNode> = ({ id: nodeId, data }) => {
	const [complexSettingForm, setComplexSettingForm] = useRecoilState(
		ComplexSettingFormState
	);
	const pipelinesSteps = useRecoilValue(PipelineMasterSteps);
	const setNodes = useSetRecoilState(NodesState);
	const setEdges = useSetRecoilState(EdgesState);
	// The same gate the row above uses, so per-level prices appear and disappear
	// with every other price on the tile.
	const { isPricingHide } = useOnboardingHook();

	const selectDefaultLabel = useMemo(
		() => complexSettingForm?.[nodeId]?.accreditationType,
		[complexSettingForm, nodeId]
	);

	const actionId = useMemo(() => data.label, [data.label]);

	const actions = useMemo(
		() =>
			pipelinesSteps.find(
				pipelineStep => pipelineStep.key === ActionList[actionId]
			)?.actions[0],
		[actionId, pipelinesSteps]
	);

	// __simplici_accreditation_506c_label__: matched on `label` before, which
	// made the tile's price hostage to the copy -- a flow saved under an older
	// label stopped matching and silently fell back to the action price. `value`
	// is the stable key (it is what billing and the backend match on); the label
	// is kept only as a fallback for anything stored before values were written.
	const price = useMemo(() => {
		const options = (actions?.metadata?.[0]?.options ?? []) as any[];
		const chosen =
			options.find(el => el?.value === (selectDefaultLabel as any)?.value) ??
			options.find(el => el?.label === selectDefaultLabel?.label);
		return chosen?.price ?? actions?.price;
	}, [actions?.metadata, actions?.price, selectDefaultLabel]);

	// __simplici_accreditation_price_range__: with "Investor chooses" the level is
	// not decided until the session runs, so there is no single figure to quote --
	// it is the 506b rate if they self-certify and the 506c rate if they do not.
	// Both bounds are read from the options' RESOLVED prices, so a business on
	// non-standard rates gets its own range rather than the list one.
	const priceRange = useMemo(() => {
		const chosen = (selectDefaultLabel as any)?.value;
		if (chosen !== 'investorChoice') return undefined;
		const priced = ((actions?.metadata?.[0]?.options ?? []) as any[])
			.map(o => Number(o?.price))
			.filter(n => Number.isFinite(n) && n > 0);
		if (priced.length < 2) return undefined;
		return { min: Math.min(...priced), max: Math.max(...priced) };
	}, [actions?.metadata, selectDefaultLabel]);

	// __simplici_accreditation_tile_choice__: the levels, from the same catalog
	// metadata the gear modal's select reads -- including their resolved prices,
	// so the two entry points cannot disagree about what a level costs.
	const options = useMemo(
		() => (actions?.metadata?.[0]?.options ?? []) as any[],
		[actions?.metadata]
	);

	// Write the default level on mount. complex-publish-api serialises
	// ComplexSettingFormState[nodeId] verbatim, so without this a tile nobody
	// opened the gear on published no accreditationType at all -- while showing
	// "Self Accreditation (506b)". Merge, never overwrite: a node that already
	// carries a choice keeps it. accreditationDocument is seeded alongside for
	// the same reason the KYC tile seeds its own keys -- creating the node's
	// settings object here defeats SelectComponent's `!complexSettingForm?.[id]`
	// guard, so anything that relied on that guard has to be seeded here too.
	useEffect(() => {
		const meta = (actions?.metadata ?? []) as any[];
		const level = meta[0]?.options?.[0];
		const document = meta[1]?.options?.[0];
		if (!level) return;
		setComplexSettingForm((prev: any) => {
			if (prev?.[nodeId]?.accreditationType) return prev;
			const next = structuredClone(prev);
			if (!next[nodeId]) next[nodeId] = {};
			next[nodeId].accreditationType = level;
			if (document && next[nodeId].accreditationDocument == null) {
				next[nodeId].accreditationDocument = document;
			}
			return next;
		});
	}, [actions?.metadata, nodeId, setComplexSettingForm]);

	// __simplici_accreditation_no_conditions__: the step takes no condition
	// blocks at all now, so nothing here can create one -- but a flow built
	// before that could, and those blocks would otherwise sit on the node unseen
	// with their edges dangling. Changing the level drops them. Identical to the
	// gear modal's onRemoveAccreditationBlock.
	const clearConditionBlocks = useCallback(() => {
		setNodes((prev: any) => {
			const next = structuredClone(prev);
			const index = next.findIndex((el: any) => el.id === nodeId);
			if (index !== -1) next[index].conditions = [];
			return next;
		});
		setEdges((prev: any) =>
			structuredClone(prev).filter(
				(el: any) => !(el.source === nodeId && el.source !== el.sourceHandle)
			)
		);
	}, [nodeId, setEdges, setNodes]);

	const onChooseLevel = useCallback(
		(value: string) => {
			const option = options.find(el => el?.value === value);
			if (!option) return;
			clearConditionBlocks();
			setComplexSettingForm((prev: any) => {
				const next = structuredClone(prev);
				if (!next[nodeId]) next[nodeId] = {};
				next[nodeId].accreditationType = option;
				// 506c has no self-certification document to upload; the modal
				// resets this the same way, so both entry points agree.
				if (value === '506c' && next[nodeId].accreditationDocument) {
					next[nodeId].accreditationDocument = {
						label: 'Generic',
						value: 'generic',
					};
				}
				return next;
			});
		},
		[clearConditionBlocks, nodeId, options, setComplexSettingForm]
	);

	// The chooser is its own block UNDER the name row, not the name row itself:
	// dressing that slate pill as a control made it read as neither -- a tag that
	// happened to be clickable. The row stays what it has always been (what this
	// step is, and what it costs), and all three levels are laid out below it with
	// what each one bills, so the whole decision is on the face of the tile.
	//
	// `nodrag` + stopPropagation: the tile is a react-flow node, and without them
	// clicking a radio starts a node drag instead. With fewer than two levels
	// there is nothing to choose, so nothing renders -- which is also the state
	// before /pipeline-init has answered.
	const levelSelect = useMemo(() => {
		if (options.length < 2) return undefined;
		const selected = selectDefaultLabel?.value ?? options[0]?.value;
		// A priceless level (the investor decides) is quoted as the span of the
		// levels that DO have prices -- the same figures the row above and the
		// Actions List quote, resolved against this business's own rates.
		const priced = options
			.map(option => Number(option?.price))
			.filter(amount => Number.isFinite(amount) && amount > 0);
		const lo = priced.length ? Math.min(...priced) : 0;
		const hi = priced.length ? Math.max(...priced) : 0;
		return (
			<div className="accreditation-action__level" role="radiogroup" aria-label="Accreditation level">
				<div className="accreditation-action__level-label">
					Accreditation level
				</div>
				{options.map(option => {
					const own = Number(option?.price);
					const amount = Number.isFinite(own)
						? `$${own.toFixed(2)}`
						: hi > lo
						? `$${lo.toFixed(2)} – $${hi.toFixed(2)}`
						: `$${lo.toFixed(2)}`;
					return (
						<label
							className="accreditation-action__level-row"
							key={option?.value}
						>
							<input
								type="radio"
								className="nodrag"
								name={`accreditation-level-${nodeId}`}
								value={option?.value}
								checked={option?.value === selected}
								onChange={() => onChooseLevel(option?.value)}
								onClick={event => event.stopPropagation()}
							/>
							<span className="accreditation-action__level-name">
								{option?.label}
							</span>
							{isPricingHide && (
								<span className="accreditation-action__level-price">
									{amount}
									<span className="accreditation-action__level-unit">
										/Verification
									</span>
								</span>
							)}
						</label>
					);
				})}
			</div>
		);
	}, [isPricingHide, nodeId, onChooseLevel, options, selectDefaultLabel]);

	return (
		<AccreditationWrapper>
			<RenderAccBlock
				label={selectDefaultLabel?.label ?? 'Self Accreditation (506b)'}
				control={levelSelect}
				price={priceRange ? priceRange.min : price}
				priceMax={priceRange?.max}
				stepKey={selectDefaultLabel?.value ?? '506b'}
				nodeId={nodeId}
				stepId="accreditation"
			/>
			<TileTotal
				amount={priceRange ? priceRange.min : price}
				max={priceRange?.max}
				nodeId={nodeId}
			/>
			<CustomHandle value={nodeId} />
		</AccreditationWrapper>
	);
};
